import {
  ClientUserRole,
  UserInvite,
  UserInviteAccounts,
} from '@data/users/model/user'
import { UserInviteLocation } from '@data/users/model/userManagement'

// Split out of `users.ts` so it can be tested without pulling in the axios client, which
// touches localStorage at module load. Same arrangement as `mapUser` and `mapAlerts`.

export interface InvitationAccountResponse {
  accountIdRef: number
  invitationIdRef: number
  admin: boolean
  autoAddLocations: boolean
  autoAddAlerts: boolean
  invoice: boolean
  position: ClientUserRole
}

export interface InvitationLocationResponse {
  locationIdRef: number
  invitationIdRef: number
  admin: boolean
  alerts: boolean
  emergencyContact: boolean
}

export interface InvitationResponse {
  id: number
  email: string
  active: boolean
  createdAt: string
  updatedAt: string
  accounts: InvitationAccountResponse[]
  locations?: InvitationLocationResponse[]
  // The wire carries these as integers (`list[int]` on `UserInvitePublic`). They were
  // typed as `string[]` here, which was a lie TypeScript could not catch and which broke
  // the edit modal's group pre-selection — see the mapper below.
  groupIds?: number[]
  roleIds?: number[]
  permissionIds?: number[]
  welcomeEmail?: boolean
  expiryDate: string
  accountCreated: boolean
}

export const mapInvitation = (invitation: InvitationResponse): UserInvite => ({
  id: invitation.id,
  email: invitation.email,
  active: invitation.active,
  createAt: invitation.createdAt,
  updatedAt: invitation.updatedAt,
  accounts: invitation.accounts.map(
    (account): UserInviteAccounts => ({
      accountId: String(account.accountIdRef),
      invitationIdRef: account.invitationIdRef,
      admin: account.admin,
      autoAddLocations: account.autoAddLocations,
      autoAddAlerts: account.autoAddAlerts,
      invoice: account.invoice,
      accountContact: false,
      position: account.position,
    }),
  ),
  locations: invitation.locations?.map(
    (location): UserInviteLocation => ({
      locationId: String(location.locationIdRef),
      invitationIdRef: location.invitationIdRef,
      admin: location.admin,
      alerts: location.alerts,
      emergencyContact: location.emergencyContact,
    }),
  ),
  // Stringified to match `Group.id`, which `mapGroup` builds with `String(id)`. The edit
  // modal pre-selects groups with `groupIds.includes(g.id)`; comparing raw numbers against
  // those strings never matched, so no group showed as selected and saving the reopened
  // modal sent `groupIds: []` and wiped the assignment.
  // Consistent with `accountId` and `locationId` above, which are stringified the same way.
  groupIds: invitation.groupIds?.map(String),
  roleIds: invitation.roleIds,
  permissionIds: invitation.permissionIds,
  welcomeEmail: invitation.welcomeEmail,
  expiryDate: invitation.expiryDate,
  accountCreated: invitation.accountCreated,
})
